import type { ReactNode } from "react";
import { Cpu, Gauge, Loader2 } from "lucide-react";
import { useOpsManager, useOpsProcesses } from "../../hooks/queries";
import { capacityRows } from "./opsDashboard";
import type { OpsProcess } from "../../types/contract";
import { ErrorBoundary } from "../../components/ErrorBoundary";

export function Panel({
  title,
  icon,
  children,
  state,
}: {
  title: string;
  icon: ReactNode;
  children?: ReactNode;
  state?: { isLoading: boolean; isError: boolean };
}) {
  return (
    <section className="flex min-h-0 flex-col rounded-lg border border-line bg-surface">
      <h2 className="flex items-center gap-1.5 border-b border-line px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
        {icon} {title}
      </h2>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {/*
 Per-panel boundary: the dashboard promises panels fail INDEPENDENTLY, and a render fault in one
            (a surprise payload shape, say) must cost that panel only -- not blank the whole page.
*/}
        {state?.isLoading ? (
          <p className="flex items-center gap-1.5 text-[11px] text-muted">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </p>
        ) : state?.isError ? (
          // Honest per-panel failure: this endpoint may simply not exist yet (the agent requests are open)
          // or the caller isn't actually an admin. Either way the panel says so instead of showing zeros.
          <p className="text-[11px] text-faint">
            Unavailable — this data source isn’t reachable right now.
          </p>
        ) : (
          <ErrorBoundary label={`system dashboard: ${title}`} resetKey={title}>
            {children}
          </ErrorBoundary>
        )}
      </div>
    </section>
  );
}

export function Stat({
  label,
  value,
  icon,
}: {
  label: string;
  value: number | undefined;
  icon: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 rounded border border-line bg-raised px-3 py-2">
      <span className="text-faint">{icon}</span>
      <div>
        {/* a missing count renders as —, never 0: "zero users" and "we couldn't count" are different facts */}
        <div className="text-lg font-semibold tabular-nums text-fg">
          {value == null ? "—" : value.toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-wide text-faint">
          {label}
        </div>
      </div>
    </div>
  );
}

function processIsDrifting(p: OpsProcess): boolean {
  return (
    p.drift === true ||
    (typeof p.drift === "string" && Boolean(p.drift) && p.drift !== "none")
  );
}

export function ProcessesPanel({ enabled }: { enabled: boolean }) {
  const procs = useOpsProcesses(enabled);
  const rows = Array.isArray(procs.data) ? procs.data : [];
  return (
    <Panel
      title="Processes — documented vs observed"
      icon={<Cpu size={12} />}
      state={{ isLoading: procs.isLoading, isError: procs.isError }}
    >
      {rows.length === 0 ? (
        <p className="text-[11px] text-faint">No processes reported.</p>
      ) : (
        <table className="w-full text-[11px]">
          <thead className="text-[10px] uppercase tracking-wide text-faint">
            <tr>
              <th className="py-1 text-left font-medium">Process</th>
              <th className="py-1 text-right font-medium">Fixed s</th>
              <th className="py-1 text-right font-medium">Observed</th>
              <th className="py-1 text-right font-medium">s/grid</th>
              <th className="py-1 text-right font-medium">Observed</th>
              <th className="py-1 text-right font-medium">n</th>
              <th className="py-1 text-right font-medium">Drift</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p: OpsProcess) => {
              // drift + drift_ratio are BE-Light's server-side verdict (>=5 samples, ratio outside
              // 0.67-1.5x). The FE renders it and deliberately does NOT recompute a second definition.
              const drifting = processIsDrifting(p);
              return (
                <tr
                  key={p.process_id}
                  className={drifting ? "text-amber-500" : "text-muted"}
                >
                  <td className="py-0.5 pr-2">
                    {p.name ?? p.process_id}
                    {drifting && (
                      <span className="ml-1 text-[9px] uppercase">drift</span>
                    )}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">
                    {p.fixed_s ?? "—"}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">
                    {p.observed_fixed_s ?? "—"}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">
                    {p.s_per_grid ?? "—"}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">
                    {p.observed_s_per_grid ?? "—"}
                  </td>
                  <td className="py-0.5 text-right tabular-nums">
                    {p.n_samples ?? "—"}
                  </td>
                  {/* BE-Light's ratio, not a second FE computation of the same thing */}
                  <td className="py-0.5 text-right tabular-nums">
                    {typeof p.drift_ratio === "number"
                      ? `${p.drift_ratio.toFixed(1)}×`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

export function CapacityPanel({ enabled }: { enabled: boolean }) {
  const manager = useOpsManager(enabled);
  const rows = capacityRows(manager.data?.rows?.[0]?.capacity);
  return (
    <Panel
      title="Resource utilisation"
      icon={<Gauge size={12} />}
      state={{ isLoading: manager.isLoading, isError: manager.isError }}
    >
      {rows.length === 0 ? (
        <p className="text-[11px] text-faint">No capacity ledger reported.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => {
            const pct =
              r.total > 0 ? Math.min(100, (r.used / r.total) * 100) : 0;
            const full = r.total > 0 && r.used >= r.total;
            return (
              <li key={r.key}>
                <div className="flex items-baseline justify-between text-[11px]">
                  <span className={r.scarce ? "text-fg" : "text-muted"}>
                    {r.label}
                    {/* scarce tokens are the ones that gate external APIs — worth marking */}
                    {r.scarce && (
                      <span className="ml-1 text-[9px] uppercase text-faint">
                        scarce
                      </span>
                    )}
                  </span>
                  <span
                    className={`tabular-nums ${full ? "text-amber-500" : "text-faint"}`}
                  >
                    {r.used} / {r.total}
                    {full && (
                      <span className="ml-1 text-[9px] uppercase">
                        saturated
                      </span>
                    )}
                  </span>
                </div>
                <div className="mt-0.5 h-1 w-full overflow-hidden rounded bg-raised">
                  <div
                    className={`h-full ${full ? "bg-amber-500" : "bg-accent"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
